// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-05-27 14:18:05 +0200 (Wed, 27 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163319 $
//  Purpose   : tree react-hooks
// =============================================================================
// ---Third-party imports
import React, { createContext, useContext } from 'react';
// ---Framework imports
import { useEffectSSR as useEffect, useState, useSelector, useSelectorShallow, useActions, useMemo } from 'uix/hooks';
import { get, dict, isDigit, isString } from 'ldash';
import log from 'uix/logging';
import { Actions, Sel } from 'uix/registries';
// ---Local imports
import { rules, NTYPE, buildStaticDomainNodes } from './rules';
// ---Exports
const ancestorAxis = (root)=>get(root.data.parts, 'Unbound.ancestorAxisJson.data.ancestorAxis');
export const useTreeRules = ()=>{
    let rulesProps = useSelectorShallow((root)=>({
            treeById: root.data.tree.byId,
            treeDetailsById: root.data.tree.detailsById,
            treeChildData: root.data.tree.childData,
            iconfig: root.config.instance,
            loadedDomains: root.config.ddata,
            classCfgs: Sel.domainClassCfgs(root),
            i18n: Sel.i18n(root),
            domains: Sel.domains(root),
            request: Sel.request(root)
        }));
    let treeRules = useMemo(()=>rules(rulesProps), [
        rulesProps
    ]);
    return treeRules;
};
export const useReqTreeNode = (enabled)=>{
    let { id: reqid } = useSelector(Sel.request);
    let rules = useTreeRules();
    let [getTreeDetails, setTreeNodeData] = useActions([
        Actions.getTreeDetails,
        Actions.setTreeNodeData
    ]);
    let domain = useSelector(Sel.domain);
    let domains = useSelector(Sel.domains);
    let isStaticDomainNode = false;
    let otid = reqid;
    if (!reqid) {
        otid = domain.otid;
    // fallback to otid of domain
    } else if (isString(reqid) && !isDigit(reqid)) {
        // non-string otid
        // also go into datatype 10 mode as we need to build a domain node
        otid = domain.otid;
        isStaticDomainNode = reqid.includes('~');
    }
    let { details, nodeData, ancestorAxis } = useSelectorShallow((root)=>({
            details: root.data.tree.detailsById[otid],
            nodeData: root.data.tree.byId[otid],
            ancestorAxis: Sel.ancestorAxis(root)
        }));
    if (!ancestorAxis || ancestorAxis.length == 0) {
        ancestorAxis = [
            {
                datatype: null,
                subtype: null,
                odid: null,
                otlink: null
            }
        ];
    }
    var { datatype, odid, subtype, otlink } = ancestorAxis[0];
    useEffect(()=>{
        // domain dt=10 is not supported here
        // XXX: not tree details for links?
        if (enabled && otid && !details && datatype !== null && datatype !== 10 && !otlink) {
            getTreeDetails(otid, datatype);
        }
        if (!nodeData && datatype !== null) {
            let { odid, caption, datatype, subtype, delstate } = ancestorAxis[0];
            setTreeNodeData(otid, odid, caption, datatype, subtype, delstate);
        }
    }, [
        otid,
        datatype,
        enabled
    ]);
    let node = useMemo(()=>{
        if (!rules) {
            return null;
        }
        if (datatype == 10) {
            // use fallback from above
            let dnode = rules[NTYPE.DOMAIN].buildSingleNode({
                dname: domain.name
            });
            if (isStaticDomainNode) {
                let nodes = buildStaticDomainNodes(rules, dnode);
                let staticNode = nodes.filter((n)=>n.id == reqid)[0];
                if (staticNode) {
                    return staticNode;
                }
            }
            return dnode;
        } else {
            // add dname translation, not in default node data of vltype
            return {
                ...rules[NTYPE.VLTYPE].buildSingleNode(otid),
                dname: domains.odid2name[odid]
            };
        }
    }, [
        rules,
        ancestorAxis
    ]);
    return node;
};
